/**
 * Task Context
 *
 * Manages task state for the Task Management system:
 * - Tasks grouped by project
 * - Current task in "doing" status (one per agent constraint)
 * - Assigned tasks list
 * - Filter state for task views
 *
 * @module contexts/TaskContext
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect
} from 'react';

// ============================================
// Constants
// ============================================

/**
 * Task status values
 */
export const TASK_STATUS = {
  BACKLOG: 'backlog',
  TODO: 'todo',
  DOING: 'doing',
  AGENT_TEST: 'agent_test',
  USER_TEST: 'user_test',
  REVIEW: 'review',
  TESTING: 'testing',
  DONE: 'done',
  BLOCKED: 'blocked'
};

/**
 * Task priority values
 */
export const TASK_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Action types for the reducer
 */
const ACTION_TYPES = {
  SET_TASKS: 'SET_TASKS',
  ADD_TASK: 'ADD_TASK',
  UPDATE_TASK: 'UPDATE_TASK',
  DELETE_TASK: 'DELETE_TASK',
  SET_CURRENT_TASK: 'SET_CURRENT_TASK',
  CLEAR_CURRENT_TASK: 'CLEAR_CURRENT_TASK',
  SET_ASSIGNED_TASKS: 'SET_ASSIGNED_TASKS',
  UPDATE_FILTERS: 'UPDATE_FILTERS',
  RESET_FILTERS: 'RESET_FILTERS',
  SET_ACTIVE_PROJECT: 'SET_ACTIVE_PROJECT',
  SET_ACTIVE_FEATURE: 'SET_ACTIVE_FEATURE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// ============================================
// Initial State
// ============================================

const initialState = {
  // Tasks grouped by project ID
  tasksByProject: {},

  // Currently active project ID
  activeProjectId: null,

  // Currently active feature ID (for filtering)
  activeFeatureId: null,

  // The one task in "doing" status (constraint from SPEC)
  currentTask: null,

  // All tasks assigned to current user/agent
  assignedTasks: [],

  // Filter state for task views
  filters: {
    status: null,      // TaskStatus[] | null
    priority: null,    // Priority[] | null
    labels: null,      // string[] | null
    assignee: null,    // string | null
    searchQuery: ''
  },

  // Loading state
  isLoading: false,

  // Error state
  error: null
};

// ============================================
// Reducer
// ============================================

function taskReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_TASKS: {
      const { projectId, tasks } = action.payload;
      return {
        ...state,
        tasksByProject: {
          ...state.tasksByProject,
          [projectId]: tasks
        },
        isLoading: false,
        error: null
      };
    }

    case ACTION_TYPES.ADD_TASK: {
      const { projectId, task } = action.payload;
      const existingTasks = state.tasksByProject[projectId] || [];
      return {
        ...state,
        tasksByProject: {
          ...state.tasksByProject,
          [projectId]: [...existingTasks, task]
        }
      };
    }

    case ACTION_TYPES.UPDATE_TASK: {
      const { projectId, taskId, updates } = action.payload;
      const existingTasks = state.tasksByProject[projectId] || [];
      const updatedTasks = existingTasks.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      );

      // Also update currentTask if it's the same task
      let updatedCurrentTask = state.currentTask;
      if (state.currentTask?.id === taskId) {
        updatedCurrentTask = { ...state.currentTask, ...updates };
        // Clear currentTask if status changed from 'doing'
        if (updates.status && updates.status !== TASK_STATUS.DOING) {
          updatedCurrentTask = null;
        }
      }

      // Update assignedTasks if applicable
      const updatedAssignedTasks = state.assignedTasks.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      );

      return {
        ...state,
        tasksByProject: {
          ...state.tasksByProject,
          [projectId]: updatedTasks
        },
        currentTask: updatedCurrentTask,
        assignedTasks: updatedAssignedTasks
      };
    }

    case ACTION_TYPES.DELETE_TASK: {
      const { projectId, taskId } = action.payload;
      const existingTasks = state.tasksByProject[projectId] || [];
      const filteredTasks = existingTasks.filter(task => task.id !== taskId);

      return {
        ...state,
        tasksByProject: {
          ...state.tasksByProject,
          [projectId]: filteredTasks
        },
        currentTask: state.currentTask?.id === taskId ? null : state.currentTask,
        assignedTasks: state.assignedTasks.filter(task => task.id !== taskId)
      };
    }

    case ACTION_TYPES.SET_CURRENT_TASK: {
      return {
        ...state,
        currentTask: action.payload
      };
    }

    case ACTION_TYPES.CLEAR_CURRENT_TASK: {
      return {
        ...state,
        currentTask: null
      };
    }

    case ACTION_TYPES.SET_ASSIGNED_TASKS: {
      return {
        ...state,
        assignedTasks: action.payload
      };
    }

    case ACTION_TYPES.UPDATE_FILTERS: {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        }
      };
    }

    case ACTION_TYPES.RESET_FILTERS: {
      return {
        ...state,
        filters: initialState.filters
      };
    }

    case ACTION_TYPES.SET_ACTIVE_PROJECT: {
      return {
        ...state,
        activeProjectId: action.payload,
        activeFeatureId: null // Reset feature when project changes
      };
    }

    case ACTION_TYPES.SET_ACTIVE_FEATURE: {
      return {
        ...state,
        activeFeatureId: action.payload
      };
    }

    case ACTION_TYPES.SET_LOADING: {
      return {
        ...state,
        isLoading: action.payload
      };
    }

    case ACTION_TYPES.SET_ERROR: {
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    }

    case ACTION_TYPES.CLEAR_ERROR: {
      return {
        ...state,
        error: null
      };
    }

    default:
      return state;
  }
}

// ============================================
// Context
// ============================================

const TaskContext = createContext(null);

// ============================================
// Provider Component
// ============================================

/**
 * TaskProvider Component
 *
 * Provides task state and actions to the application.
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} Provider component
 */
export function TaskProvider({ children }) {
  const [state, dispatch] = useReducer(taskReducer, initialState);

  // ----------------------------------------
  // Actions
  // ----------------------------------------

  const setTasks = useCallback((projectId, tasks) => {
    dispatch({
      type: ACTION_TYPES.SET_TASKS,
      payload: { projectId, tasks }
    });
  }, []);

  const addTask = useCallback((projectId, task) => {
    dispatch({
      type: ACTION_TYPES.ADD_TASK,
      payload: { projectId, task }
    });
  }, []);

  const updateTask = useCallback((projectId, taskId, updates) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_TASK,
      payload: { projectId, taskId, updates }
    });
  }, []);

  const deleteTask = useCallback((projectId, taskId) => {
    dispatch({
      type: ACTION_TYPES.DELETE_TASK,
      payload: { projectId, taskId }
    });
  }, []);

  const setCurrentTask = useCallback((task) => {
    dispatch({
      type: ACTION_TYPES.SET_CURRENT_TASK,
      payload: task
    });
  }, []);

  const clearCurrentTask = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_CURRENT_TASK });
  }, []);

  const setAssignedTasks = useCallback((tasks) => {
    dispatch({
      type: ACTION_TYPES.SET_ASSIGNED_TASKS,
      payload: tasks
    });
  }, []);

  const updateFilters = useCallback((filterUpdates) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_FILTERS,
      payload: filterUpdates
    });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET_FILTERS });
  }, []);

  const setActiveProject = useCallback((projectId) => {
    dispatch({
      type: ACTION_TYPES.SET_ACTIVE_PROJECT,
      payload: projectId
    });
  }, []);

  const setActiveFeature = useCallback((featureId) => {
    dispatch({
      type: ACTION_TYPES.SET_ACTIVE_FEATURE,
      payload: featureId
    });
  }, []);

  const setLoading = useCallback((isLoading) => {
    dispatch({
      type: ACTION_TYPES.SET_LOADING,
      payload: isLoading
    });
  }, []);

  const setError = useCallback((error) => {
    dispatch({
      type: ACTION_TYPES.SET_ERROR,
      payload: error
    });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_ERROR });
  }, []);

  // ----------------------------------------
  // Selectors
  // ----------------------------------------

  /**
   * Get tasks for the active project, optionally filtered
   */
  const getFilteredTasks = useCallback(() => {
    if (!state.activeProjectId) return [];

    let tasks = state.tasksByProject[state.activeProjectId] || [];

    // Filter by feature if set
    if (state.activeFeatureId) {
      tasks = tasks.filter(task => task.featureId === state.activeFeatureId);
    }

    // Apply status filter
    if (state.filters.status?.length > 0) {
      tasks = tasks.filter(task => state.filters.status.includes(task.status));
    }

    // Apply priority filter
    if (state.filters.priority?.length > 0) {
      tasks = tasks.filter(task => state.filters.priority.includes(task.priority));
    }

    // Apply label filter
    if (state.filters.labels?.length > 0) {
      tasks = tasks.filter(task =>
        task.labels?.some(label => state.filters.labels.includes(label.name))
      );
    }

    // Apply assignee filter
    if (state.filters.assignee) {
      tasks = tasks.filter(task => task.assigneeId === state.filters.assignee);
    }

    // Apply search query
    if (state.filters.searchQuery) {
      const query = state.filters.searchQuery.toLowerCase();
      tasks = tasks.filter(task =>
        task.title.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query)
      );
    }

    return tasks;
  }, [state]);

  /**
   * Get tasks grouped by status (for Kanban view)
   */
  const getTasksByStatus = useCallback(() => {
    const tasks = getFilteredTasks();
    const grouped = {};

    Object.values(TASK_STATUS).forEach(status => {
      grouped[status] = tasks.filter(task => task.status === status);
    });

    return grouped;
  }, [getFilteredTasks]);

  // ----------------------------------------
  // Context Value
  // ----------------------------------------

  const value = useMemo(() => ({
    // State
    ...state,

    // Actions
    setTasks,
    addTask,
    updateTask,
    deleteTask,
    setCurrentTask,
    clearCurrentTask,
    setAssignedTasks,
    updateFilters,
    resetFilters,
    setActiveProject,
    setActiveFeature,
    setLoading,
    setError,
    clearError,

    // Selectors
    getFilteredTasks,
    getTasksByStatus
  }), [
    state,
    setTasks,
    addTask,
    updateTask,
    deleteTask,
    setCurrentTask,
    clearCurrentTask,
    setAssignedTasks,
    updateFilters,
    resetFilters,
    setActiveProject,
    setActiveFeature,
    setLoading,
    setError,
    clearError,
    getFilteredTasks,
    getTasksByStatus
  ]);

  return (
    <TaskContext.Provider value={value}>
      {children}
    </TaskContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

/**
 * Hook to access task context
 * @returns {object} Task context value
 */
export function useTask() {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTask must be used within TaskProvider');
  }
  return context;
}

export default TaskContext;
